/**
 * Pure operations on a deck's `sources` array (`SyncDeckMeta.sources`) and
 * the integrity of card source refs that point into those sources.
 *
 * All functions are side-effect free: they return new arrays / patch lists
 * and leave persistence to the platform sync layer (`sync.decks.*`).
 */

import type {
  DeckSource,
  DeckSourceTombstones,
} from '../../sync-engine/schema/shared-types';
import { isAnchoredCardSourceRef, parseCardSourceRef } from './card-source-ref';

/** Minimal card shape these helpers need. */
export interface SourceRefCard {
  id: string;
  source?: string;
}

/** The deck's primary source id — unqualified refs resolve against it. */
export function primarySourceId(
  sources: readonly DeckSource[] | undefined,
): string | undefined {
  return sources?.[0]?.sourceId;
}

/**
 * Identity of the deck's primary source for cache keys: which source it is
 * AND what it points at. The id alone is not enough — a deck whose "no
 * document" record is replaced by its real document keeps the same
 * `sourceId`, and a key that did not change would keep serving "no
 * document" until the cache expired.
 */
export function primarySourceIdentity(
  sources: readonly DeckSource[] | undefined,
): string | null {
  const primary = sources?.[0];
  if (!primary) return null;
  return [primary.sourceId, primary.kind, primary.pdfInfo?.filePath ?? ''].join('|');
}

export function findSource(
  sources: readonly DeckSource[] | undefined,
  sourceId: string,
): DeckSource | undefined {
  return sources?.find((s) => s.sourceId === sourceId);
}

/**
 * Append a source. If the id is already present the existing entry is
 * replaced in place (idempotent re-attach), otherwise the entry is added
 * at the end — position 0 (the primary source) is never displaced.
 */
export function addSource(
  sources: readonly DeckSource[] | undefined,
  next: DeckSource,
): DeckSource[] {
  const list = [...(sources ?? [])];
  const existing = list.findIndex((s) => s.sourceId === next.sourceId);
  if (existing >= 0) {
    list[existing] = next;
    return list;
  }
  list.push(next);
  return list;
}

/**
 * A placeholder is an entry that carries NO material: the `manual` entry a
 * deck born without material writes at creation, or a HOLLOW entry — no file
 * and no text (`sourceLabel` empty or `MANUAL`). The hollow shape is what a
 * read-side backfill used to record for a deck whose server source was
 * `{ source: '' }` (a deck created a moment before its documents landed), as
 * `kind: 'text'` with nothing to read. The `apkg` label an Anki import is
 * created with (`apkgImportSource`) is one too: it says where the cards came
 * from, not a document. None is a document: nothing was ever anchored in it
 * and nothing can be shown for it. (A `gpt` entry is left alone: a described
 * deck's type, and with it its own Generate entry, is read from it.)
 */
export function isPlaceholderSource(source: DeckSource): boolean {
  if (source.kind === 'manual') return true;
  if (source.pdfInfo?.filePath) return false;
  if (source.kind === 'apkg') return true;
  const label = source.sourceLabel?.trim() ?? '';
  return label === '' || label === 'MANUAL';
}

/**
 * The list without its placeholder(s). A deck born without material (the
 * onboarding first deck, "create manually", a Ka-created deck) carries a
 * `manual` entry at creation so `useGetSource` short-circuits and the
 * Document tab shows its upload form; a deck read before its first document
 * landed may carry a hollow `text` entry instead ({@link isPlaceholderSource}).
 * A placeholder is NOT a document: it must leave the moment a real source
 * lands, or it stays `sources[0]` — the primary the whole deck UI keys on
 * (`primarySourceId`, `deckTabCapabilities`, `earlyPdfFilePath` all read
 * index 0) — and the deck reads as source-less to every consumer with its
 * real documents parked behind it. EVERY attach path (the single legacy
 * attach, the batch planner the upload queue lands through) drops it through
 * this one rule.
 */
export function withoutManualPlaceholder(
  sources: readonly DeckSource[] | undefined,
): DeckSource[] {
  return (sources ?? []).filter((s) => !isPlaceholderSource(s));
}

/**
 * Sources array after the first REAL source lands on a deck born without
 * material: the `manual` placeholder is dropped ({@link withoutManualPlaceholder})
 * so the new source becomes `sources[0]`. A plain `addSource` would append at
 * index 1 behind the placeholder and the deck would stay source-less to
 * every consumer. The batch planner (`planAttachDeckSources`) applies the
 * same rule.
 */
export function addSourceReplacingPlaceholder(
  sources: readonly DeckSource[] | undefined,
  next: DeckSource,
): DeckSource[] {
  return addSource(withoutManualPlaceholder(sources), next);
}

/**
 * Give a source the user's own name. Only `title` changes — the file, its
 * path, the legacy `sourceLabel` and the entry's position all stay, so
 * nothing that addresses the document (card refs, the viewer, the primary
 * slot) can be disturbed by a rename. An unknown id returns the list as is.
 */
export function renameSource(
  sources: readonly DeckSource[] | undefined,
  sourceId: string,
  title: string,
  /** The rename's clock — lets it win per entry against an older edit of the
   *  same document made on another device. */
  nowISO?: string,
): DeckSource[] {
  return (sources ?? []).map((s) =>
    s.sourceId === sourceId
      ? { ...s, title, ...(nowISO ? { updatedAtISO: nowISO } : {}) }
      : s,
  );
}

/** The later of two ISO stamps (`undefined` loses). */
function laterISO(a: string, b: string | undefined): string {
  return b !== undefined && b > a ? b : a;
}

/** One millisecond after `iso` — the smallest stamp that outranks it. */
function justAfterISO(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : new Date(ms + 1).toISOString();
}

/**
 * The clock that keeps an entry ALIVE against a removal: when it was added,
 * or when an Undo last put it back. A rename or a drag never counts — see
 * `mergeDeckSourceList`.
 */
export function sourceAliveClock(
  source: Pick<DeckSource, 'addedAtISO' | 'restoredAtISO'>,
): string {
  return laterISO(source.addedAtISO ?? '', source.restoredAtISO);
}

/**
 * Record the removal of `sourceIds` at `nowISO` (`SyncDeckMeta.sourceTombstones`).
 * Every write that takes a document OUT of `sources` must carry this beside
 * the shortened list: two rows that meet union `sources` by id, and without
 * the tombstone the other device's copy simply puts the document back.
 *
 * Pass the list the documents are removed FROM: a removal is stamped strictly
 * after every clock the entry carries, so a document added (or restored, or
 * renamed) on a device whose clock runs ahead is still outranked by it.
 */
export function withSourceTombstones(
  tombstones: DeckSourceTombstones | undefined,
  sourceIds: readonly string[],
  nowISO: string,
  sources?: readonly DeckSource[],
): DeckSourceTombstones {
  const next: DeckSourceTombstones = { ...tombstones };
  for (const id of sourceIds) {
    const entry = findSource(sources, id);
    let at = laterISO(nowISO, next[id]);
    if (entry) {
      at = laterISO(at, justAfterISO(sourceAliveClock(entry)));
      at = laterISO(at, justAfterISO(entry.updatedAtISO));
    }
    next[id] = at;
  }
  return next;
}

/** The fields of a deck row {@link restoreClockAfter} reads. */
export interface SourceRemovalRecord {
  id?: string;
  deckPaths?: readonly string[];
  deckName?: string;
  sourceTombstones?: DeckSourceTombstones;
  sourcesClearedAtISO?: string;
}

/**
 * The `restoredAtISO` an Undo of `source` stamps: `nowISO`, or — when a
 * device's clock ran ahead — strictly after every removal the row records
 * that could keep it out: its own tombstone and, for the deck's LEGACY
 * document only (no `origin`, keyed by one of the deck's own keys), the
 * tombstones under those keys and the emptied marker. The last two are what
 * the merge measures the legacy document against (an older app build's
 * refill must never pass for an Undo), so restoring THAT document clears
 * them too. Any other document is measured against its own removal alone:
 * inheriting a fast clock's marker would stamp its Undo in the future, and
 * every later rename of it would then lose the per-entry merge. The Undo
 * always outranks the removal it undoes.
 */
export function restoreClockAfter(
  row: SourceRemovalRecord,
  source: string | Pick<DeckSource, 'sourceId' | 'origin'>,
  nowISO: string,
): string {
  const { sourceId, origin } =
    typeof source === 'string' ? { sourceId: source, origin: undefined } : source;
  const tombstones = row.sourceTombstones ?? {};
  const deckKeys = [row.id, row.deckName, ...(row.deckPaths ?? [])].filter(
    (key): key is string => !!key,
  );
  let at = laterISO(nowISO, justAfterISO(tombstones[sourceId]));
  if (origin || !deckKeys.includes(sourceId)) return at;
  for (const key of deckKeys) at = laterISO(at, justAfterISO(tombstones[key]));
  return laterISO(at, justAfterISO(row.sourcesClearedAtISO));
}

export interface RemoveSourceResult {
  sources: DeckSource[];
  /**
   * Patches for cards whose refs pointed into the removed source. Their
   * `source` becomes `'manual'` — the long-standing sentinel for "no
   * source link" — so they keep working everywhere (study, edit, export)
   * and simply stop rendering source highlights.
   */
  cardPatches: Array<{ id: string; source: string }>;
}

export function removeSource(
  sources: readonly DeckSource[] | undefined,
  cards: readonly SourceRefCard[],
  sourceId: string,
): RemoveSourceResult {
  const list = sources ?? [];
  const primary = primarySourceId(list);
  const nextSources = list.filter((s) => s.sourceId !== sourceId);

  // The same reading of "which document" the badge and the materials pane
  // use (`materialRefForCard`): a legacy chunk ref belongs to the primary, so
  // removing the primary unlinks those cards too instead of leaving them to
  // wear the next document's name.
  const cardPatches: Array<{ id: string; source: string }> = [];
  for (const card of cards) {
    const ref = parseCardSourceRef(card.source);
    if (!isAnchoredCardSourceRef(ref)) continue;
    const refSource = ref.sourceId ?? primary;
    if (refSource !== sourceId) continue;
    cardPatches.push({ id: card.id, source: 'manual' });
  }

  return { sources: nextSources, cardPatches };
}

export interface RemovePagesResult {
  sources: DeckSource[];
  /** Pages actually removed (0-based original-document indices). */
  removedPages: number[];
}

/**
 * Remove a set of pages from a PDF-backed source. `pdfInfo.pages` holds
 * the 0-based ORIGINAL page indices that are part of the deck (the upload
 * flow already works on a subset), so removal is pure set subtraction.
 *
 * Card text anchors are character offsets into the FULL extraction
 * content, so they remain valid for every page that stays; anchors on
 * removed pages simply stop rendering (their page is no longer shown)
 * while the flashcards themselves keep working. Use
 * {@link cardsOnRemovedPages} to surface an advisory count in the UI.
 */
export function removePagesFromSource(
  sources: readonly DeckSource[] | undefined,
  sourceId: string,
  pagesToRemove: readonly number[],
  /** The edit's clock. Copies of the document merge per entry on this clock,
   *  so without it an older edit made elsewhere (a rename) wins the entry and
   *  the removed pages come back. */
  nowISO: string = new Date().toISOString(),
): RemovePagesResult {
  const list = [...(sources ?? [])];
  const idx = list.findIndex((s) => s.sourceId === sourceId);
  const entry = idx >= 0 ? list[idx] : undefined;
  if (!entry?.pdfInfo?.pages) {
    return { sources: list, removedPages: [] };
  }
  const removeSet = new Set(pagesToRemove);
  const keptPages = entry.pdfInfo.pages.filter((p) => !removeSet.has(p));
  const removedPages = entry.pdfInfo.pages.filter((p) => removeSet.has(p));
  if (removedPages.length === 0) {
    return { sources: list, removedPages: [] };
  }
  list[idx] = {
    ...entry,
    pdfInfo: { ...entry.pdfInfo, pages: keptPages },
    updatedAtISO: nowISO,
  };
  return { sources: list, removedPages };
}

/** Character span of one extraction page within the full content. */
export interface PageSpan {
  /** 1-based extraction page number. */
  pageNumber: number;
  start: number;
  end: number;
}

/**
 * Cards whose anchors land ENTIRELY on the given (about to be removed)
 * pages. `pageSpans` comes from the extraction (`page.spans`); `pages`
 * are 0-based original indices like `pdfInfo.pages`.
 */
export function cardsOnRemovedPages(
  cards: readonly SourceRefCard[],
  pageSpans: readonly PageSpan[],
  removedPages: readonly number[],
  opts: { sourceId: string; primarySourceId: string | undefined },
): SourceRefCard[] {
  const removedSet = new Set(removedPages.map((p) => p + 1)); // 1-based
  const removedSpans = pageSpans.filter((s) => removedSet.has(s.pageNumber));
  if (removedSpans.length === 0) return [];

  const result: SourceRefCard[] = [];
  for (const card of cards) {
    const ref = parseCardSourceRef(card.source);
    if (ref.type === 'text-range') {
      const refSource = ref.sourceId ?? opts.primarySourceId;
      if (refSource !== undefined && refSource !== opts.sourceId) continue;
      const coveredByRemoved = removedSpans.some(
        (span) => ref.start >= span.start && ref.end <= span.end,
      );
      if (coveredByRemoved) result.push(card);
    } else if (ref.type === 'page-rect') {
      const refSource = ref.sourceId ?? opts.primarySourceId;
      if (refSource !== undefined && refSource !== opts.sourceId) continue;
      if (removedSet.has(ref.pageNumber)) result.push(card);
    }
  }
  return result;
}
